import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './clientPayments.css';
import ClientInvoicesHeader from './ClientInvoicesHeader';
import ReceiptKindChip from '../ReceiptKindChip';
import { dubaiYMD, fmtPaymentMoment } from '../../../utils/dubaiTime';

function ClientPayments() {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await axios.get('/api/client-requests/invoices/payments');
      const fetchedPayments = response.data.payments || [];

      setPayments(fetchedPayments);

    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Asia/Dubai day (item 8) — toISOString() gave the UTC day. Same
  // YYYY-MM-DD visual.
  const formatDate = (dateString) => (dateString ? dubaiYMD(dateString) : '');

  return (
    <div className="client-payments-section">

      {/* 🔥 Reusable Header Component */}
      <ClientInvoicesHeader
        title="Payments Received"
        pendingAmount={null} // 🚫 No pending amount for payments page
        isLoading={isLoading}
      />

      {/* Main Content */}
      {isLoading ? (
        <div className="app-loading">
          <div className="app-spinner" />
          <div className="app-spinner-text">Loading Payments...</div>
        </div>
      ) : payments.length === 0 ? (
        <div className="app-loading">
          <p className="client-payments-empty">No payments found.</p>
        </div>
      ) : (
        <div className="client-payments-list">
          {payments.map((payment) => (
            <div
              key={payment.id}
              className="client-payment-card"
            >
              <div className="client-payment-info">
                <p>
                  <strong>Payment ID:</strong> #{payment.id}
                  {payment.receipt_kind && (
                    <span style={{ marginLeft: 8 }}><ReceiptKindChip kind={payment.receipt_kind} /></span>
                  )}
                  {/* Billing entity (multi-billing clients only — entity_label
                      is absent for everyone else). */}
                  {payment.entity_label && (
                    <span className="client-payment-entity">{payment.entity_label}</span>
                  )}
                </p>
                <p><strong>Date:</strong> {fmtPaymentMoment(payment.created_time) || formatDate(payment.date)}</p>
                <p><strong>Amount:</strong> AED {Number(payment.amount || 0).toFixed(2)}</p>
                <p><strong>Payment Method:</strong> {payment.paymentMethod}</p>
                {(payment.bookingId || payment.booking_id) ? (
                  <p>
                    <strong>Booking:</strong>{' '}
                    <Link className="client-payment-link" to={`/client/bookings/${payment.bookingId || payment.booking_id}`}>
                      #{payment.bookingId || payment.booking_id}
                    </Link>
                  </p>
                ) : payment.request_id ? (
                  // Paid for a booking request that isn't approved yet — link to
                  // the request; it becomes a Booking link once confirmed.
                  <p>
                    <strong>Booking Request:</strong>{' '}
                    <Link className="client-payment-link" to={`/client/bookings/requests/${payment.request_id}`}>
                      REQ-{payment.request_id}
                    </Link>
                  </p>
                ) : null}
                <p><strong>Paid Invoices:</strong> {payment.invoiceIds && payment.invoiceIds.length > 0 ? (
                  payment.invoiceIds.map((invId, index) => (
                    <span key={invId}>
                      <Link className="client-payment-link" to="/client/invoices">#{invId}</Link>
                      {index !== payment.invoiceIds.length - 1 ? ', ' : ''}
                    </span>
                  ))
                ) : (
                  <span>None</span>
                )}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClientPayments;
